import React, {Fragment, useContext, useState, useEffect, useRef} from 'react';
import {ButtonGroup, Button} from '@blueprintjs/core';

import {ElemsContext} from '@auravisionlabs/aura-react';

import ShapesContexts from './shape-contexts';
import {EditShapesHeader} from './header';
import {MenuListElement} from './list-element';
import {LineContexts} from "./line-contexts";


function MenuEditShapes({
    currentActiveMenuSection: initialSection,
    loadingTaxonomies,
    taxonomiesInUse,
    keysAndValuesInUse,
    beforeChange,
    afterChange,
    onElemsAction,
}) {
    const {elems, selected, recording, setSelected, setHovered, fetch} = useContext(ElemsContext);
    const [currentActiveMenuSection, setCurrentActiveMenuSection] = useState(initialSection || 'areas');

    const prevSelectedRef = useRef(null);
    const prevElemsRef = useRef(null);

    // Auto-switch tab when selection changes
    useEffect(() => {
        if (selected !== prevSelectedRef.current || elems !== prevElemsRef.current) {
            prevSelectedRef.current = selected;
            prevElemsRef.current = elems;
            if (selected && elems) {
                const nextSection =
                    elems.areas.some(a => a.meOrMyChild(selected)) ? 'areas'
                        : elems.curves.some(l => l.meOrMyChild(selected)) ? 'lines'
                            : currentActiveMenuSection;
                handleSectionChange(nextSection);
            }
        }
    }, [elems, selected]);

    function handleSectionChange(option) {
        if (currentActiveMenuSection !== option) {
            const url = new URL(window.location.href);
            url.searchParams.set("currentActiveMenuSection", option);
            window.history.pushState({}, '', url.toString());
            setCurrentActiveMenuSection(option);
        }
    }

    function handleCurrentMenuOpen(elem) {
        setSelected(selected === elem ? null : elem);
    }

    return (
        <Fragment>
            <ButtonGroup fill={true}>
                <Button
                    onClick={() => handleSectionChange('areas')}
                    icon="polygon-filter"
                    intent={currentActiveMenuSection === 'areas' ? 'primary' : 'none'}
                >
                    Areas
                </Button>
                <Button
                    onClick={() => handleSectionChange('lines')}
                    icon="flow-linear"
                    intent={currentActiveMenuSection === 'lines' ? 'primary' : 'none'}
                >
                    Lines
                </Button>
                <Button
                    onClick={() => handleSectionChange('area_contexts')}
                    icon="send-to-graph"
                    intent={currentActiveMenuSection === 'area_contexts' ? 'primary' : 'none'}
                >
                    Area contexts
                </Button>
                <Button
                    onClick={() => handleSectionChange('line_contexts')}
                    icon="new-link"
                    intent={currentActiveMenuSection === 'line_contexts' ? 'primary' : 'none'}
                >
                    Line contexts
                </Button>
            </ButtonGroup>
            <EditShapesHeader
                type={currentActiveMenuSection}
                title={{
                    'areas': 'Areas',
                    'lines': 'Lines',
                    'area_contexts': 'Area contexts',
                    'line_contexts': 'Line Contexts',
                }[currentActiveMenuSection]}
                createNewShape={(type, name) => {
                    if (type === 'lines' || type === 'areas') {
                        beforeChange();
                        elems.addDefault(type, name);
                        afterChange();
                    }
                }}
            />
            {elems && currentActiveMenuSection === 'areas' &&
                elems.areas.map((elem, index) => {
                    return <MenuListElement
                        key={`${elem.name}-${index}`}
                        elem={elem}
                        isOpen={selected && elem.meOrMyChild(selected)}
                        beforeChange={beforeChange}
                        afterChange={afterChange}
                        handleCurrentMenuOpen={handleCurrentMenuOpen}
                    />
                })
            }

            {elems && currentActiveMenuSection === 'lines' &&
                elems.curves.map((elem, index) => {
                    return <MenuListElement
                        key={`${elem.name}-${index}`}
                        elem={elem}
                        isOpen={selected && elem.meOrMyChild(selected)}
                        beforeChange={beforeChange}
                        afterChange={afterChange}
                        handleCurrentMenuOpen={handleCurrentMenuOpen}
                    />
                })
            }
            {elems && currentActiveMenuSection === "area_contexts" && (
                <ShapesContexts
                    recording={recording}
                    elems={elems}
                    taxonomiesInUse={taxonomiesInUse}
                    loadingTaxonomies={loadingTaxonomies}
                    updateEditRecordingPageState={() => {
                        onElemsAction({action: 'save-changes'})
                    }}
                    setShapeHighlight={(elem, highlight) => {
                        setHovered(highlight ? elem : null);
                    }}
                />
            )}
            {currentActiveMenuSection === "line_contexts" && (
                <LineContexts
                    recording={recording}
                    elems={elems}
                    taxonomiesInUse={taxonomiesInUse}
                    keysAndValuesInUse={keysAndValuesInUse}
                    loadingTaxonomies={loadingTaxonomies}
                    updateEditRecordingPageState={() => {
                        onElemsAction({action: 'save-changes'})
                        fetch(recording.id)
                    }}
                    setShapeHighlight={(elem, highlight) => {
                        setHovered(highlight ? elem : null);
                    }}
                />
            )}
        </Fragment>
    );
}

export {MenuEditShapes}
